import { MutationFunction } from '@apollo/client';
import { Checkbox, Input, Select, Stack } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { notNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';

import { Button } from '../../components/button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { Form as FormFactory } from '../../lib/form';
import {
  ConnectionParameterFieldType,
  CreateTelematicsIntegrationMutation,
  CreateTelematicsIntegrationMutationVariables,
  GraphQLJSON,
  notEmpty,
  TelematicsProvidersSimpleListQuery,
} from '../../lib/graphql';
import { IntegrationInputField } from '../containers/provider_input_container';
import { ValidateIntegrationButton } from '../containers/telematics_validation_container';
import { getConnectionData, TelematicsProviderIdentifier } from '../lib';
import { TtcIntegrationPurposeAlert, TtcShipperIdAlert } from './integration';

import styles from './styles.module.css';

export interface CreateIntegrationViewDataProps {
  data: {
    telematicsProviders: NonNullable<
      NonNullable<TelematicsProvidersSimpleListQuery['telematicsProvidersV2']['providers']['edges']>[number]
    >['node'][];
    company_id: string;
  };
}
export interface CreateIntegrationViewProps {
  createIntegration: MutationFunction<
    CreateTelematicsIntegrationMutation,
    CreateTelematicsIntegrationMutationVariables
  >;
  routeProps: {
    push: (path: string) => void;
    goBack: () => void;
  };
}

class Form extends FormFactory<
  { telematicsProvider: string; company_integration_id: string; enabled: boolean } & Record<
    string,
    string | number | boolean
  >
> {}

function isVisibleProvider({ telematicsProviderId }: { telematicsProviderId: string }) {
  return telematicsProviderId !== TelematicsProviderIdentifier.SixfoldCarrierTelematicsApi;
}

type SelectedProvider = NonNullable<CreateIntegrationViewDataProps['data']['telematicsProviders'][number]>;
type FieldValidationError = Record<string, string | undefined>;
export const CreateIntegrationView: React.FunctionComponent<
  CreateIntegrationViewDataProps & CreateIntegrationViewProps
> = ({ routeProps, createIntegration, data }) => {
  const isNotAdmin = !useIsAdmin();
  const hasIntegrationsEditPermission = useHasScopes([BackofficeScope.telematicsWriteIntegrations]);
  const isPrivilegedUser = !isNotAdmin || hasIntegrationsEditPermission;
  const [dynamicValidationErrors, setDynamicValidationErrors] = useState<FieldValidationError>({});

  const providerScoped = (provider: string, fieldName: string) => `${provider}_${fieldName}`;
  const visibleProviders = data.telematicsProviders
    .filter(notEmpty)
    .filter(({ isAddingIntegrationDisabled }) => !isAddingIntegrationDisabled)
    .filter(isVisibleProvider);

  const enumDefaultValue = (parameter: SelectedProvider['connectionParameters'][number]) => {
    return parameter.enumFieldValues.find((enumFieldValue) => enumFieldValue.is_default)?.value ?? '';
  };

  const someRequiredFieldsMissing = (
    selectedProvider: SelectedProvider,
    connectionData: GraphQLJSON,
  ): FieldValidationError | undefined => {
    const validationErrors: FieldValidationError = {};
    selectedProvider.connectionParameters
      .filter(({ isOptional }) => !isOptional)
      .forEach((parameter) => {
        const formKey = providerScoped(selectedProvider.telematicsProviderId, parameter.fieldName);
        const fieldValue = connectionData[parameter.fieldName];
        const errorMessage = `Enter ${parameter.fieldDisplayName ?? parameter.fieldName}`;

        const isMissing =
          fieldValue === undefined ||
          fieldValue === null ||
          (typeof fieldValue === 'string' && fieldValue.trim() === '');

        if (isMissing) {
          if (parameter.fieldType === ConnectionParameterFieldType.ENUM) {
            connectionData[parameter.fieldName] = enumDefaultValue(parameter);
            return;
          }
          validationErrors[formKey] = errorMessage;
        }
      });
    return Object.keys(validationErrors).length !== 0 ? validationErrors : undefined;
  };
  return (
    <Form
      initialValues={{
        telematicsProvider: '',
        company_integration_id: 'main',
        enabled: false,
      }}
      validateFields={{
        company_integration_id: {
          exist: 'Enter a company integration id',
        },
      }}
      onSubmit={async (form) => {
        const selectedProvider = visibleProviders.find(
          (provider) => provider.telematicsProviderId === form.telematicsProvider,
        );
        if (selectedProvider === undefined) {
          throw new Error(`Couldn't find selected provider '${form.telematicsProvider}`);
        }
        const connection_data = getConnectionData({
          connectionParameters: selectedProvider.connectionParameters,
          formData: form,
          currentFormFieldKeyFn: (formFieldName) =>
            providerScoped(selectedProvider.telematicsProviderId, formFieldName),
        });
        const requiredFieldsAreMissing = someRequiredFieldsMissing(selectedProvider, connection_data);

        if (requiredFieldsAreMissing !== undefined) {
          setDynamicValidationErrors(requiredFieldsAreMissing);
          return;
        }

        await createIntegration({
          variables: {
            input: {
              company_id: data.company_id,
              telematics_provider_id: selectedProvider.telematicsProviderId,
              company_integration_id: form.company_integration_id,
              enabled: form.enabled,
              connection_data,
            },
          },
        });
        routeProps.push(`/companies/${data.company_id}/integrations`);
      }}>
      {({ form, errors, onChange, onSubmit, isSubmittingForm }) => {
        const providerOptions = visibleProviders
          .sort((providerA, providerB) => {
            const x = providerA.telematicsProviderName.toLowerCase();
            const y = providerB.telematicsProviderName.toLowerCase();
            if (x < y) {
              return -1;
            }
            if (x > y) {
              return 1;
            }
            return 0;
          })
          .map((provider) => ({
            label: provider.isDeprecated
              ? `${provider.telematicsProviderName} (deprecated)`
              : provider.telematicsProviderName,
            value: provider.telematicsProviderId,
          }));

        const getErrorMessage = (
          fieldKey: string,
          staticValidation: FieldValidationError,
          dynamicValidation: FieldValidationError,
        ): string | undefined => {
          if (staticValidation[fieldKey] !== undefined) {
            return staticValidation[fieldKey];
          }
          if (dynamicValidation[fieldKey] !== undefined) {
            return dynamicValidation[fieldKey];
          }
          return undefined;
        };
        const selectedProvider = visibleProviders.find(
          (provider) => provider.telematicsProviderId === form.telematicsProvider,
        );
        const isSelectedProviderDeprecated = selectedProvider?.isDeprecated ?? false;
        return (
          <form onSubmit={onSubmit}>
            <Stack direction="column" gap="large" style={{ maxWidth: 500 }}>
              <Stack direction="column" gap="small">
                <div className={styles.deprecatedProviderDropdown}>
                  <Select
                    label="Telematics provider"
                    labelPosition="block"
                    isFilterable
                    placeholder=" -- select a provider -- "
                    className={isSelectedProviderDeprecated ? 'deprecated-selected' : undefined}
                    options={providerOptions}
                    value={providerOptions.find((option) => option.value === form.telematicsProvider)}
                    onChange={(option) => onChange({ value: option?.value, name: 'telematicsProvider' })}
                  />
                </div>
                {form.telematicsProvider === TelematicsProviderIdentifier.Ttc && <TtcIntegrationPurposeAlert />}
              </Stack>

              {form.telematicsProvider !== ''
                ? data.telematicsProviders
                    .filter(notEmpty)
                    .filter((provider) => provider.telematicsProviderId === form.telematicsProvider)[0]
                    .connectionParameters.filter(notEmpty)
                    .map((setting) => {
                      const displayName = setting.fieldDisplayName ?? setting.fieldName;
                      const scopedFieldName = providerScoped(form.telematicsProvider, setting.fieldName);
                      const inputValue = () => {
                        if (setting.fieldType === ConnectionParameterFieldType.ENUM) {
                          return notNil(form[scopedFieldName])
                            ? form[scopedFieldName].toString()
                            : enumDefaultValue(setting);
                        }
                        return (form[scopedFieldName] ?? '').toString();
                      };
                      const hasError = getErrorMessage(scopedFieldName, errors, dynamicValidationErrors) !== undefined;
                      const labelText = hasError
                        ? (getErrorMessage(scopedFieldName, errors, dynamicValidationErrors) ?? displayName)
                        : `${displayName}${setting.isOptional ? ' (optional)' : ''}${
                            setting.isInternal ? ' (internal)' : ''
                          }`;

                      return (
                        <Stack key={scopedFieldName} direction="column" gap="small">
                          <IntegrationInputField
                            label={labelText}
                            fieldType={setting.fieldType}
                            settingsValue={inputValue()}
                            fieldName={scopedFieldName}
                            disabled={!isPrivilegedUser}
                            defaultChecked={form[scopedFieldName] === true}
                            enumFieldValues={setting.enumFieldValues ?? []}
                            onChange={onChange}
                            hasError={hasError}
                          />
                          {setting.fieldName === 'shipper_id' &&
                            form.telematicsProvider === TelematicsProviderIdentifier.Ttc && <TtcShipperIdAlert />}
                        </Stack>
                      );
                    })
                : null}
              {selectedProvider !== undefined && (
                <Input
                  label={errors.company_integration_id ?? 'Company integration id'}
                  name="company_integration_id"
                  value={form.company_integration_id}
                  disabled={!isPrivilegedUser}
                  onChange={({ value }) => onChange({ value, name: 'company_integration_id' })}
                />
              )}
              {selectedProvider !== undefined && (
                <Checkbox
                  label="Enabled"
                  name="enabled"
                  checked={form.enabled}
                  disabled={!isPrivilegedUser}
                  onChange={({ checked }) => onChange({ value: checked, name: 'enabled' })}
                />
              )}
            </Stack>
            {selectedProvider !== undefined && (
              <Stack direction="row" gap="small" style={{ marginTop: 'var(--size-spacing-large)' }}>
                <Button type="submit" kind="primary" disabled={!isPrivilegedUser || isSubmittingForm}>
                  Add
                </Button>
                <Button type="button" onClick={() => routeProps.goBack()}>
                  Cancel
                </Button>

                <ValidateIntegrationButton
                  integration={{
                    telematics_provider_id: selectedProvider.telematicsProviderId,
                    connection_data: getConnectionData({
                      connectionParameters: selectedProvider.connectionParameters,
                      formData: form,
                      currentFormFieldKeyFn: (formFieldName) =>
                        providerScoped(selectedProvider.telematicsProviderId, formFieldName),
                    }),
                  }}
                />
              </Stack>
            )}
          </form>
        );
      }}
    </Form>
  );
};
